import { useMutation, useQuery, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import { authFetch } from "@/auth/session";

import { api, unwrap } from "./client";
import type { components } from "./schema";

type Schemas = components["schemas"];
export type QuestionSummary = Schemas["QuestionSummary"];
export type QuestionDetail = Schemas["QuestionDetail"];
export type ClusterOut = Schemas["ClusterOut"];
export type JobOut = Schemas["JobOut"];
export type TransitionOut = Schemas["TransitionOut"];
export type DecisionOut = Schemas["DecisionOut"];
export type MetricsOut = Schemas["MetricsOut"];
export type EditRequest = Schemas["EditRequest"];
export type ReclusterResult = Schemas["ReclusterResult"];
export type PurgeRejectedResult = Schemas["PurgeRejectedResult"];
export type RetryFailedResult = Schemas["RetryFailedResult"];
export type ConfigOut = Schemas["ConfigOut"];
export type DocumentStat = Schemas["DocumentStat"];
export type DocumentDetailOut = Schemas["DocumentDetailOut"];
export type DocumentTransitionOut = Schemas["DocumentTransitionOut"];
export type ProbeResult = Schemas["ProbeResult"];
export type ProbeNeighbour = Schemas["ProbeNeighbour"];
export type TaxonomyEntryOut = Schemas["TaxonomyEntryOut"];
export type PickRequest = Schemas["PickRequest"];
export type PickOut = Schemas["PickOut"];
export type PickBucket = Schemas["PickBucket"];
export type PickItem = Schemas["PickItem"];
export type PickQuestion = Schemas["PickQuestion"];
export type TaxonomyKind = "stack" | "concept" | "bucket";
export type StackSummaryOut = Schemas["StackSummaryOut"];
export type StackOverviewOut = Schemas["StackOverviewOut"];
export type StackDetailOut = Schemas["StackDetailOut"];
export type ConceptBreakdown = Schemas["ConceptBreakdown"];
export type StackExportBatchOut = Schemas["StackExportBatchOut"];
export type StackExportItemOut = Schemas["StackExportItemOut"];
export type StackExportBatchRequest = Schemas["StackExportBatchRequest"];
export type GenerateRequest = Schemas["GenerateRequest"];
export type GenerationTemplateOut = Schemas["GenerationTemplateOut"];
export type GenerationPreviewOut = Schemas["GenerationPreviewOut"];
export type PendingReviewOut = Schemas["PendingReviewOut"];
export type DraftsOut = Schemas["DraftsOut"];
export type ReviewDraftsOut = Schemas["ReviewDraftsOut"];
export type AccessOut = Schemas["AccessOut"];
export type RoleOut = AccessOut["roles"][number];
export type Role = string;
export type DirectoryMember = Schemas["DirectoryMemberOut"];
export type PermissionCatalogItemOut = NonNullable<AccessOut["catalog"]>[number] & {
  parent_page?: string | null;
};
export type ExperienceBandOut = Schemas["ExperienceBandOut"];
export type ExperienceBandIn = Schemas["ExperienceBandIn"];
export type LinkedQuestion = Schemas["LinkedQuestion"];
export type SplitInfo = Schemas["SplitInfo"];
export type SplitProposalOut = Schemas["SplitProposalOut"];
export type SplitPreviewOut = Schemas["SplitPreviewOut"];
export type SplitDistributionOut = Schemas["SplitDistributionOut"];
export type SplitJobRequest = Schemas["SplitJobRequest"];
export type SplitState = "needs_split" | "proposal" | "exempt" | "split";

/**
 * Query keys, in one place.
 *
 * Every mutation below invalidates by prefix rather than naming exact keys: approving
 * a question changes its row, the review queue, the status counts and possibly a
 * cluster, and listing those individually is how a stale table survives an action
 * that should have refreshed it.
 */
export const keys = {
  questions: ["questions"] as const,
  question: (id: number) => ["questions", id] as const,
  history: (id: number) => ["questions", id, "history"] as const,
  decisions: (id: number) => ["questions", id, "decisions"] as const,
  clusters: ["clusters"] as const,
  cluster: (canonicalId: number) => [...keys.clusters, canonicalId] as const,
  jobs: ["jobs"] as const,
  job: (id: number) => ["jobs", id] as const,
  status: ["status"] as const,
  metrics: ["metrics"] as const,
  health: ["health"] as const,
  facets: ["facets"] as const,
  documents: ["documents"] as const,
  document: (sourceDoc: string) => [...keys.documents, sourceDoc] as const,
  config: ["config"] as const,
  probe: (jobId: number) => ["probe", jobId] as const,
  taxonomy: (kind: TaxonomyKind) => ["taxonomy", kind] as const,
  stackOverview: (bucket?: string, includeInactive?: boolean) =>
    ["stack-overview", bucket ?? null, includeInactive ?? false] as const,
  stackDetail: (stackPath: string) => ["stack-overview", "detail", stackPath] as const,
  access: ["access"] as const,
  memberDirectory: ["directory"] as const,
  experienceBands: ["experience-bands"] as const,
  primaryShare: ["experience-bands", "primary-share"] as const,
};

export interface QuestionFilters {
  status?: string[];
  stack_path?: string;
  bucket?: string;
  primary_concept?: string;
  source_doc?: string;
  search?: string;
  difficulty_score?: number;
  question_type?: string;
  /** DESIGN §17.11.6 — the follow-up link, from either end. */
  has_follow_up?: boolean;
  is_follow_up?: boolean;
  split_state?: SplitState;
  /** DESIGN §20.7 — a question some JD (or `jd_id`'s) has as its own, and who may be served it. */
  from_jd?: boolean;
  jd_id?: number;
  scope?: "bank" | "jd_only";
  limit?: number;
  offset?: number;
}

export function useQuestions(filters: QuestionFilters) {
  return useQuery({
    queryKey: [...keys.questions, filters],
    queryFn: async () =>
      unwrap(
        await api.GET("/questions", {
          params: {
            query: {
              status: filters.status ?? null,
              stack_path: filters.stack_path ?? null,
              bucket: filters.bucket ?? null,
              primary_concept: filters.primary_concept ?? null,
              source_doc: filters.source_doc ?? null,
              search: filters.search ?? null,
              difficulty_score: filters.difficulty_score ?? null,
              question_type: filters.question_type ?? null,
              has_follow_up: filters.has_follow_up ?? null,
              is_follow_up: filters.is_follow_up ?? null,
              split_state: filters.split_state ?? null,
              from_jd: filters.from_jd ?? null,
              jd_id: filters.jd_id ?? null,
              scope: filters.scope ?? null,
              limit: filters.limit ?? 50,
              offset: filters.offset ?? 0,
            },
          },
        }),
      ),
    // A reviewer paging back and forth should not see the table blank between pages.
    placeholderData: (previous) => previous,
  });
}

export function useQuestion(id: number | null) {
  return useQuery({
    queryKey: keys.question(id ?? 0),
    queryFn: async () => unwrap(await api.GET("/questions/{qid}", { params: { path: { qid: id! } } })),
    enabled: id !== null,
  });
}

export function useHistory(id: number | null) {
  return useQuery({
    queryKey: keys.history(id ?? 0),
    queryFn: async () =>
      unwrap(await api.GET("/questions/{qid}/history", { params: { path: { qid: id! } } })),
    enabled: id !== null,
  });
}

export function useDecisions(id: number | null) {
  return useQuery({
    queryKey: keys.decisions(id ?? 0),
    queryFn: async () =>
      unwrap(await api.GET("/questions/{qid}/decisions", { params: { path: { qid: id! } } })),
    enabled: id !== null,
  });
}

export interface ClusterFilters {
  status?: string[];
  stack_path?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export function useClusters(filters: ClusterFilters) {
  return useQuery({
    queryKey: [...keys.clusters, filters],
    queryFn: async () =>
      unwrap(
        await api.GET("/clusters", {
          params: {
            query: {
              status: filters.status ?? null,
              stack_path: filters.stack_path ?? null,
              search: filters.search ?? null,
              limit: filters.limit ?? 25,
              offset: filters.offset ?? 0,
            },
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
}

/**
 * One cluster, by its canonical's id — the same assembled shape `useClusters` pages
 * through, fetched for a single question's detail view instead of a whole page of
 * them. A variant's `canonical_id` and a canonical's own `id` both work as input.
 */
export function useCluster(canonicalId: number | null) {
  return useQuery({
    queryKey: keys.cluster(canonicalId ?? 0),
    queryFn: async () =>
      unwrap(await api.GET("/clusters/{canonical_id}", { params: { path: { canonical_id: canonicalId! } } })),
    enabled: canonicalId !== null,
  });
}

/**
 * Polled lightly so the sidebar's pipeline-backlog count drains on its own while a
 * job runs — the alternative is a reviewer staring at a stale number until some
 * unrelated mutation happens to invalidate it.
 */
export function useStatusCounts() {
  return useQuery({
    queryKey: keys.status,
    queryFn: async () => unwrap(await api.GET("/status", {})),
    refetchInterval: 15_000,
  });
}

export function useMetrics() {
  return useQuery({
    queryKey: keys.metrics,
    queryFn: async () => unwrap(await api.GET("/metrics", {})),
  });
}

/**
 * The values behind the stack and concept filters.
 *
 * Long `staleTime` on purpose: these change when the bank gains a concept, which is
 * a pipeline run away, not a keystroke away. Refetching them per render would put two
 * DISTINCT scans behind every character typed into the search box.
 */
export function useFacets() {
  return useQuery({
    queryKey: keys.facets,
    queryFn: async () => unwrap(await api.GET("/questions/facets", {})),
    staleTime: 5 * 60_000,
  });
}

export function useDocuments() {
  return useQuery({
    queryKey: keys.documents,
    queryFn: async () => unwrap(await api.GET("/ingest/documents", {})),
  });
}

/** Reached a stage the pipeline runs unattended — raw chunking or extract/enrich/
 * embed/dedup. Worth polling fast: the worker may advance this within seconds. */
const AUTOMATED_QUESTION_STATUSES = [
  "extracted",
  "split_checked",
  "enriched",
  "embedded",
  "dedup_checked",
] as const;

/**
 * Where one upload's live status sits, from its `DocumentStat`/`DocumentDetailOut`
 * counts — drives both `useDocument`'s poll rate and the run page's state chip.
 *
 * "review": nothing left but `pending_review`/`stale` — only a human moves this now,
 * so it is polled the way `useStatusCounts` polls the sidebar, not the way a running
 * job is. "settled": every question reached approved/duplicate/rejected/deprecated/
 * failed — nothing here changes again without a reviewer action elsewhere.
 */
export function documentPhase(
  doc: Pick<DocumentStat, "chunks" | "questions">,
): "automated" | "review" | "settled" {
  if ((doc.chunks.raw ?? 0) > 0) return "automated";
  if (AUTOMATED_QUESTION_STATUSES.some((st) => (doc.questions[st] ?? 0) > 0)) return "automated";
  if ((doc.questions.pending_review ?? 0) > 0 || (doc.questions.stale ?? 0) > 0) return "review";
  return "settled";
}

/** One upload's live pipeline status — the run page's primary poll target. */
export function useDocument(sourceDoc: string | null) {
  return useQuery({
    queryKey: keys.document(sourceDoc ?? ""),
    queryFn: async () =>
      unwrap(
        await api.GET("/ingest/documents/{source_doc}", {
          params: { path: { source_doc: sourceDoc! } },
        }),
      ),
    enabled: sourceDoc !== null,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2_000;
      const phase = documentPhase(data);
      return phase === "automated" ? 2_000 : phase === "review" ? 15_000 : false;
    },
  });
}

/** Models, dedup config_version and what is left of today's quota (§8.6). */
export function useConfig() {
  return useQuery({
    queryKey: keys.config,
    queryFn: async () => unwrap(await api.GET("/config", {})),
    staleTime: 60_000,
  });
}

/**
 * A controlled vocabulary, read live — ARCHITECTURE §12. No staleTime: unlike
 * useFacets (a keystroke shouldn't refetch what the bank already contains), the whole
 * point of taxonomy_entries is that adding a stack or concept on the master page is
 * usable elsewhere immediately, with no restart.
 */
export function useTaxonomy(kind: TaxonomyKind, includeInactive = false) {
  return useQuery({
    queryKey: [...keys.taxonomy(kind), includeInactive],
    queryFn: async () =>
      unwrap(
        await api.GET("/taxonomy/{kind}", {
          params: { path: { kind }, query: { include_inactive: includeInactive } },
        }),
      ),
  });
}

export function useHealth() {
  return useQuery({
    queryKey: keys.health,
    queryFn: async () => unwrap(await api.GET("/health", {})),
    refetchInterval: 30_000,
  });
}

/**
 * Jobs, polled while any of them is unfinished.
 *
 * §8 invariant 01: the API returns a job id and the client polls, so this hook is the
 * client half of that contract. It stops polling once nothing is running, because a
 * bank that has been idle overnight should not be waking the browser every two
 * seconds to hear the same answer.
 */
export function useJobs(limit = 10, kind?: string) {
  return useQuery({
    queryKey: [...keys.jobs, limit, kind ?? null],
    queryFn: async () => unwrap(await api.GET("/jobs", { params: { query: { limit, kind: kind ?? null } } })),
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      return items.some((j) => j.status === "queued" || j.status === "running") ? 2_000 : false;
    },
  });
}

/**
 * One job, polled while it is queued or running — what a person who just pressed Run is watching.
 * `useJobs` polls the list the same way, but a progress strip wants this one job's totals every
 * couple of seconds without paging through the table to find it.
 */
export function useJob(jobId: number | null) {
  return useQuery({
    queryKey: keys.job(jobId ?? 0),
    queryFn: async () =>
      unwrap(await api.GET("/jobs/{job_id}", { params: { path: { job_id: jobId ?? 0 } } })),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === undefined || status === "queued" || status === "running" ? 1_500 : false;
    },
  });
}

export interface StackOverviewFilters {
  bucket?: string | undefined;
  includeInactive?: boolean | undefined;
}

export function useStackOverview(filters?: StackOverviewFilters) {
  return useQuery({
    queryKey: keys.stackOverview(filters?.bucket, filters?.includeInactive),
    queryFn: async () =>
      unwrap(
        await api.GET("/stack-overview", {
          params: {
            query: {
              bucket: filters?.bucket ?? null,
              include_inactive: filters?.includeInactive ?? false,
            },
          },
        }),
      ),
  });
}

export function useStackDetail(stackPath: string | null) {
  return useQuery({
    queryKey: keys.stackDetail(stackPath ?? ""),
    queryFn: async () =>
      unwrap(
        await api.GET("/stack-overview/{stack_path}", {
          params: { path: { stack_path: stackPath! } },
        }),
      ),
    enabled: stackPath !== null && stackPath.length > 0,
  });
}

/**
 * Download approved questions for a technology stack as a real Microsoft Word DOCX document.
 *
 * Invokes browser download directly using the server-provided filename in Content-Disposition.
 */
export async function downloadStackQuestionsDocx(stackPath: string): Promise<string> {
  const encodedPath = encodeURIComponent(stackPath);
  const response = await authFetch(
    new Request(`/api/stack-overview/${encodedPath}/export-docx`, {
      method: "GET",
      headers: {
        Accept: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      },
    }),
  );

  if (!response.ok) {
    let message = `Export failed with status ${response.status}`;
    try {
      const err = (await response.json()) as { detail?: string };
      if (err.detail) message = err.detail;
    } catch {
      // fallback
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition");
  let filename = `approved-${stackPath.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-questions.docx`;

  if (disposition) {
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      filename = decodeURIComponent(utf8Match[1]);
    } else {
      const standardMatch = disposition.match(/filename="?([^";]+)"?/i);
      if (standardMatch?.[1]) {
        filename = standardMatch[1];
      }
    }
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  }, 200);

  return filename;
}

/** Convert base64 data to a Blob. */
export function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

/** Trigger a browser file download from an in-memory Blob. */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  }, 1000);
}

/**
 * Export approved questions for multiple technologies in one batch operation.
 * Returns individual technology file payloads and status summary.
 */
export async function exportBatchStackQuestionsDocx(technologyIds: string[]): Promise<StackExportBatchOut> {
  const response = await authFetch(
    new Request("/api/stack-overview/export-docx", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ technologyIds }),
    }),
  );

  if (!response.ok) {
    let message = `Export failed with status ${response.status}`;
    try {
      const err = (await response.json()) as { detail?: string };
      if (err.detail) message = err.detail;
    } catch {
      // fallback
    }
    throw new Error(message);
  }

  return (await response.json()) as StackExportBatchOut;
}

// ---------------------------------------------------------------- mutations

/** Everything a review action can change. See the note on `keys`. */
function useReviewInvalidation() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: keys.questions });
    void client.invalidateQueries({ queryKey: keys.clusters });
    void client.invalidateQueries({ queryKey: keys.status });
    void client.invalidateQueries({ queryKey: ["stack-overview"] });
  };
}

export type QuestionScope = "bank" | "jd_only";

/** `scope` is DESIGN §20.7's "Approve to"; left out, the question keeps the scope it has. */
export function useApprove(): UseMutationResult<
  void,
  Error,
  { id: number; note?: string; scope?: QuestionScope }
> {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, note, scope }) => {
      const { error } = await api.POST("/questions/{qid}/approve", {
        params: { path: { qid: id } },
        body: { note: note ?? null, scope: scope ?? null },
      });
      if (error) throw new Error(JSON.stringify(error));
    },
    onSuccess: invalidate,
  });
}

export function useReject(): UseMutationResult<void, Error, { id: number; note?: string }> {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, note }) => {
      const { error } = await api.POST("/questions/{qid}/reject", {
        params: { path: { qid: id } },
        body: { note: note ?? null },
      });
      if (error) throw new Error(JSON.stringify(error));
    },
    onSuccess: invalidate,
  });
}

/** `stage` forces the target explicitly — "extracted" is a re-enrichment request. Left
 * unset, the backend auto-detects the first stage the row has not completed. */
export function useRequeue() {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, note, stage }: { id: number; note?: string; stage?: string | undefined }) =>
      unwrap(
        await api.POST("/questions/{qid}/requeue", {
          params: { path: { qid: id } },
          body: { note: note ?? null, stage: stage ?? null },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useEdit() {
  const invalidate = useReviewInvalidation();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: EditRequest }) => {
      const { error } = await api.PATCH("/questions/{qid}", {
        params: { path: { qid: id } },
        body: patch,
      });
      if (error) throw new Error(JSON.stringify(error));
    },
    onSuccess: (_data, { id }) => {
      invalidate();
      void client.invalidateQueries({ queryKey: keys.question(id) });
      void client.invalidateQueries({ queryKey: keys.history(id) });
    },
  });
}

export function useMerge() {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, otherId, note }: { id: number; otherId: number; note?: string }) =>
      unwrap(
        await api.POST("/questions/{qid}/merge", {
          params: { path: { qid: id } },
          body: { other_id: otherId, note: note ?? null },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useUnmerge() {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, otherId, note }: { id: number; otherId: number; note?: string }) =>
      unwrap(
        await api.POST("/questions/{qid}/unmerge", {
          params: { path: { qid: id } },
          body: { other_id: otherId, note: note ?? null },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useSoftDelete() {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) =>
      unwrap(
        await api.DELETE("/questions/{qid}", {
          params: { path: { qid: id } },
          body: { reason },
        }),
      ),
    onSuccess: invalidate,
  });
}

/** The one hard delete: every rejected question, irreversibly (orchestration.review.purge_rejected). */
export function usePurgeRejected() {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async (ids?: number[]) =>
      unwrap(await api.POST("/questions/purge-rejected", { body: { ids: ids ?? null } })),
    onSuccess: invalidate,
  });
}

/** Hard delete of the named questions in any status, irreversibly (orchestration.review.purge_questions). */
export function usePurgeQuestions() {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async (ids: number[]) => unwrap(await api.POST("/questions/purge", { body: { ids } })),
    onSuccess: invalidate,
  });
}

export function useIngest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      force,
      expectedStack,
    }: {
      file: File;
      force: boolean;
      expectedStack: string;
    }) => {
      // openapi-fetch serialises multipart from a plain object; the generated body
      // type is what keeps the field names honest.
      const { data, error } = await api.POST("/ingest", {
        body: { file: file as unknown as string, force, expected_stack: expectedStack },
        bodySerializer: () => {
          const form = new FormData();
          form.append("file", file);
          form.append("force", String(force));
          form.append("expected_stack", expectedStack);
          return form;
        },
      });
      if (error) throw new Error(JSON.stringify(error));
      if (!data) throw new Error("The API returned no body");
      return data;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.jobs });
      void client.invalidateQueries({ queryKey: keys.status });
      // A new document means new chunk rows, so the "documents seen" table is stale.
      void client.invalidateQueries({ queryKey: keys.documents });
    },
  });
}

// ---------------------------------------------------------------- AI question generation

/** The built-in prompt and the rules an edited copy must follow. Static for the life of a
 * deploy, so it is fetched once and never refetched behind a half-edited textarea. */
export function useGenerationTemplate() {
  return useQuery({
    queryKey: ["generation", "template"],
    queryFn: async () => unwrap(await api.GET("/ingest/generate/template", {})),
    staleTime: Infinity,
  });
}

/** What a run for this stack would be told already exists. A read — no model call — so it
 * is safe to run on every stack change. */
export function useGenerationPreview(stack: string) {
  return useQuery({
    queryKey: ["generation", "preview", stack],
    queryFn: async () => unwrap(await api.GET("/ingest/generate/preview", { params: { query: { stack } } })),
    enabled: stack !== "",
  });
}

export function useGenerate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: GenerateRequest) => unwrap(await api.POST("/ingest/generate", { body })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.jobs });
      void client.invalidateQueries({ queryKey: keys.status });
      // The run's rows (and so its line in the Runs table) appear once the worker has
      // made its first call, and the jobs poll below keeps refreshing until then.
      void client.invalidateQueries({ queryKey: keys.documents });
    },
  });
}

/**
 * Generate runs whose questions still wait for a keep/discard decision. They never expire, so
 * this also lists a batch someone walked away from. Polled because the worker, not this page,
 * is what makes a run's drafts appear.
 */
export function usePendingReviews() {
  return useQuery({
    queryKey: ["generation", "pending"],
    queryFn: async () => unwrap(await api.GET("/ingest/generate/pending")),
    refetchInterval: 4_000,
  });
}

export function useDrafts(jobId: number | null) {
  return useQuery({
    queryKey: ["generation", "drafts", jobId],
    queryFn: async () =>
      unwrap(await api.GET("/ingest/generate/{job_id}/drafts", { params: { path: { job_id: jobId ?? 0 } } })),
    enabled: jobId !== null,
  });
}

/**
 * Keep the listed drafts and permanently delete the rest. Keeping some queues the pipeline, so
 * the jobs, runs and status views are refreshed along with the pending list.
 */
export function useReviewDrafts() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, keepIds }: { jobId: number; keepIds: number[] }) =>
      unwrap(
        await api.POST("/ingest/generate/{job_id}/review", {
          params: { path: { job_id: jobId } },
          body: { keep_ids: keepIds },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["generation"] });
      void client.invalidateQueries({ queryKey: keys.jobs });
      void client.invalidateQueries({ queryKey: keys.status });
      void client.invalidateQueries({ queryKey: keys.documents });
    },
  });
}

// ---------------------------------------------------------------- splitting compound questions (DESIGN §17.11)

export interface SplitPreviewParams {
  limit: number;
  order: "longest_first" | "id";
  stack?: string;
  min_seconds?: number;
}

/** The batch job's dry run. A deterministic read — no model call — so it is only fetched when the
 * person asks (`enabled`), not on every keystroke in the "how many" box. */
export function useSplitPreview(params: SplitPreviewParams, enabled: boolean) {
  return useQuery({
    queryKey: ["splits", "preview", params],
    queryFn: async () =>
      unwrap(
        await api.GET("/splits/preview", {
          params: {
            query: {
              limit: params.limit,
              order: params.order,
              stack: params.stack ?? null,
              min_seconds: params.min_seconds ?? null,
            },
          },
        }),
      ),
    enabled,
    staleTime: 0,
  });
}

/** Proposals by status. Polled lightly: the worker, not this page, is what makes them appear. */
export function useSplitProposals(status: string, offset = 0, limit = 25, mine = false) {
  return useQuery({
    queryKey: ["splits", "list", status, offset, limit, mine],
    queryFn: async () =>
      unwrap(await api.GET("/splits", { params: { query: { status, limit, offset, mine } } })),
    refetchInterval: 6_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * The questions approved proposals sent back through the pipeline, and where each is now — the
 * Splits tab's live tracker. Polled fast while anything is still moving unattended (the worker
 * advances a row every few seconds), slowly once everything has settled. `jobId` picks the batch
 * (the `split` job that proposed them); without it the server answers for the one approved from last.
 */
export function useSplitInFlight(jobId?: number, mine = false) {
  return useQuery({
    queryKey: ["splits", "in-flight", jobId ?? "latest", mine],
    queryFn: async () =>
      unwrap(
        await api.GET("/splits/in-flight", {
          params: { query: { mine, ...(jobId === undefined ? {} : { job_id: jobId }) } },
        }),
      ),
    placeholderData: (previous) => previous,
    refetchInterval: (query) => (query.state.data?.active ? 2_500 : 15_000),
  });
}

/** Estimated speech time over the servable bank: the Metrics histogram, and the limit the Speech
 * chip on Questions is coloured against. */
export function useSplitDistribution() {
  return useQuery({
    queryKey: ["splits", "distribution"],
    queryFn: async () => unwrap(await api.GET("/splits/distribution", {})),
    staleTime: 60_000,
  });
}

/** Everything a split decision can change: the proposals, the questions it rewrites, the pipeline. */
function useSplitInvalidation() {
  const client = useQueryClient();
  const reviewInvalidate = useReviewInvalidation();
  return () => {
    void client.invalidateQueries({ queryKey: ["splits"] });
    void client.invalidateQueries({ queryKey: keys.jobs });
    void client.invalidateQueries({ queryKey: keys.documents });
    reviewInvalidate();
  };
}

export function useStartSplitJob() {
  const invalidate = useSplitInvalidation();
  return useMutation({
    mutationFn: async (body: SplitJobRequest) => unwrap(await api.POST("/splits/jobs", { body })),
    onSuccess: invalidate,
  });
}

export function useApproveSplit() {
  const invalidate = useSplitInvalidation();
  return useMutation({
    mutationFn: async ({
      id,
      primaryText,
      followUpText,
    }: {
      id: number;
      primaryText?: string | undefined;
      followUpText?: string | undefined;
    }) =>
      unwrap(
        await api.POST("/splits/{split_id}/approve", {
          params: { path: { split_id: id } },
          body: { primary_text: primaryText ?? null, follow_up_text: followUpText ?? null },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useRejectSplit() {
  const invalidate = useSplitInvalidation();
  return useMutation({
    mutationFn: async (id: number) =>
      unwrap(await api.POST("/splits/{split_id}/reject", { params: { path: { split_id: id } } })),
    onSuccess: invalidate,
  });
}

/** "Long on purpose": never offered for splitting again. */
export function useMarkSplitExempt() {
  const invalidate = useSplitInvalidation();
  return useMutation({
    mutationFn: async (id: number) =>
      unwrap(await api.POST("/questions/{qid}/split-exempt", { params: { path: { qid: id } } })),
    onSuccess: invalidate,
  });
}

export function useSetFollowUp() {
  const invalidate = useSplitInvalidation();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, followUpId }: { id: number; followUpId: number }) => {
      const { error } = await api.PUT("/questions/{qid}/follow-up", {
        params: { path: { qid: id } },
        body: { follow_up_id: followUpId },
      });
      if (error) throw new Error(JSON.stringify(error));
    },
    onSuccess: (_data, { id }) => {
      invalidate();
      void client.invalidateQueries({ queryKey: keys.history(id) });
    },
  });
}

export function useUnlinkFollowUp() {
  const invalidate = useSplitInvalidation();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { error } = await api.DELETE("/questions/{qid}/follow-up", { params: { path: { qid: id } } });
      if (error) throw new Error(JSON.stringify(error));
    },
    onSuccess: (_data, id) => {
      invalidate();
      void client.invalidateQueries({ queryKey: keys.history(id) });
    },
  });
}

export function useSubmitJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (kind: string) =>
      unwrap(await api.POST("/jobs/{kind}", { params: { path: { kind } } })),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.jobs }),
  });
}

export function useCancelJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: number) =>
      unwrap(await api.POST("/jobs/{job_id}/cancel", { params: { path: { job_id: jobId } } })),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.jobs }),
  });
}

/** CLI's `qbank retry-failed` (DESIGN §10.2), from the Questions screen: resets failed
 * questions/chunks below max_retries to the status they failed from. No model call —
 * reprocessing them still needs a pipeline run, which is a separate "Run pipeline". */
export function useRetryFailed() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await api.POST("/pipeline/retry-failed", {})),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.questions });
      void client.invalidateQueries({ queryKey: keys.status });
    },
  });
}

/**
 * The similarity probe — DESIGN §14.4.
 *
 * Two hooks because it is two round trips: invariant 01 means even an interactive
 * tool goes through the queue, so submitting returns a job id and the result arrives
 * on a poll. The poll is faster than the job list's because someone is watching it,
 * and it stops the moment the job reaches a terminal status.
 */
export function useSubmitProbe() {
  return useMutation({
    mutationFn: async ({ text, k }: { text: string; k: number }) =>
      unwrap(await api.POST("/probe", { body: { text, k } })),
  });
}

export function useProbe(jobId: number | null) {
  return useQuery({
    queryKey: keys.probe(jobId ?? 0),
    queryFn: async () => unwrap(await api.GET("/probe/{job_id}", { params: { path: { job_id: jobId! } } })),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.job.status;
      return status === "queued" || status === "running" ? 1_000 : false;
    },
  });
}

// ---------------------------------------------------------------- taxonomy (ARCHITECTURE §12)

function useTaxonomyInvalidation(kind: TaxonomyKind) {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: keys.taxonomy(kind) });
}

export function useCreateTaxonomyEntry(kind: TaxonomyKind) {
  const invalidate = useTaxonomyInvalidation(kind);
  return useMutation({
    mutationFn: async ({
      path,
      note,
      bucket,
      current_version,
    }: {
      path: string;
      note?: string;
      bucket?: string;
      current_version?: string;
    }) =>
      unwrap(
        await api.POST("/taxonomy/{kind}", {
          params: { path: { kind } },
          body: {
            path,
            note: note ?? null,
            bucket: bucket ?? null,
            current_version: current_version ?? null,
          },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateTaxonomyEntry(kind: TaxonomyKind) {
  const invalidate = useTaxonomyInvalidation(kind);
  return useMutation({
    mutationFn: async ({
      id,
      path,
      note,
      bucket,
      current_version,
    }: {
      id: number;
      path?: string;
      note?: string;
      bucket?: string;
      current_version?: string;
    }) =>
      unwrap(
        await api.PATCH("/taxonomy/{kind}/{entry_id}", {
          params: { path: { kind, entry_id: id } },
          body: {
            path: path ?? null,
            note: note ?? null,
            bucket: bucket ?? null,
            current_version: current_version ?? null,
          },
        }),
      ),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------- Engine 2, the picker (DESIGN §17)

/**
 * A pick writes nothing (§17.1), so unlike every other mutation here this one invalidates
 * no cache — there is no server state for it to have changed.
 *
 * The key is passed per call rather than held anywhere: /pick is the one authenticated
 * route, its caller is the external Interviewer engine, and this console is standing in for
 * that caller. Persisting the secret in the browser would be a worse trade than typing it.
 */
export function usePick() {
  return useMutation({
    mutationFn: async ({ apiKey, ...body }: PickRequest & { apiKey: string }) =>
      unwrap(
        await api.POST("/pick", {
          headers: { "X-API-Key": apiKey },
          body,
        }),
      ),
  });
}

export function useDeactivateTaxonomyEntry(kind: TaxonomyKind) {
  const invalidate = useTaxonomyInvalidation(kind);
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) =>
      unwrap(
        await api.DELETE("/taxonomy/{kind}/{entry_id}", {
          params: { path: { kind, entry_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: invalidate,
  });
}

/** Hard delete of a stack and every question tagged with it, irreversibly (orchestration.taxonomy.purge_stack). */
export function usePurgeStack() {
  const client = useQueryClient();
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async (id: number) =>
      unwrap(
        await api.DELETE("/taxonomy/{kind}/{entry_id}/purge", {
          params: { path: { kind: "stack", entry_id: id } },
        }),
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["taxonomy"] });
      invalidate();
    },
  });
}

// ---------------------------------------------------------------- Access (ARCHITECTURE §11)

/** Who holds which role. Admin-only on the API, so only asked for when the page will show it. */
export function useAccess(enabled: boolean) {
  return useQuery({
    queryKey: keys.access,
    queryFn: async () => unwrap(await api.GET("/access")),
    enabled,
  });
}

/**
 * Workspace members by name, for the Access page's picker. The API answers `available: false`
 * rather than failing when Workspace cannot be asked, so a down gateway never breaks the page.
 */
export function useMemberDirectory(enabled: boolean) {
  return useQuery({
    queryKey: keys.memberDirectory,
    queryFn: async () => unwrap(await api.GET("/directory")),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Replace one role's member list, permissions, and metadata. `expectedMembers` is what the editor
 * started from; the API answers 409 if another admin changed the list since.
 */
export function useUpdateRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      role,
      title,
      purpose,
      members,
      expectedMembers,
      permissions,
      note,
    }: {
      role: string;
      title?: string | undefined;
      purpose?: string | undefined;
      members: string[];
      expectedMembers: string[];
      permissions?: string[] | undefined;
      note?: string | undefined;
    }) =>
      unwrap(
        await api.PUT("/access/{role}", {
          params: { path: { role } },
          body: {
            title: title ?? null,
            purpose: purpose ?? null,
            members,
            expected_members: expectedMembers,
            permissions: permissions ?? null,
            note: note ?? null,
          },
        }),
      ),
    onSuccess: (saved) => {
      client.setQueryData<AccessOut>(keys.access, (old) =>
        old ? { ...old, roles: old.roles.map((r) => (r.role === saved.role ? saved : r)) } : old,
      );
      void client.invalidateQueries({ queryKey: keys.access });
      void client.invalidateQueries({ queryKey: ["me"] });
    },
    onSettled: () => void client.invalidateQueries({ queryKey: keys.access }),
  });
}

/** Create a new custom role. */
export function useCreateRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      role,
      title,
      purpose,
      cloneFrom,
      permissions,
      members,
    }: {
      role: string;
      title: string;
      purpose?: string | undefined;
      cloneFrom?: string | undefined;
      permissions?: string[] | undefined;
      members?: string[] | undefined;
    }) =>
      unwrap(
        await api.POST("/access/roles", {
          body: {
            role,
            title,
            purpose: purpose ?? "",
            clone_from: cloneFrom ?? null,
            permissions: permissions ?? null,
            members: members ?? null,
          },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.access });
      void client.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

/** Delete a custom role. */
export function useDeleteRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (role: string) =>
      unwrap(
        await api.DELETE("/access/{role}", {
          params: { path: { role } },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.access });
      void client.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

/** Reset a role's permissions to default system presets. */
export function useResetRoleDefaults() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (role: string) =>
      unwrap(
        await api.POST("/access/{role}/reset-defaults", {
          params: { path: { role } },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.access });
      void client.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// ---------------------------------------------------------------- Experience bands (DESIGN §17.5)

/** The experience -> difficulty table `/pick` reads on every call. */
export function useExperienceBands() {
  return useQuery({
    queryKey: keys.experienceBands,
    queryFn: async () => unwrap(await api.GET("/experience-bands")),
  });
}

/** Replace the whole table; the API refuses an invalid one with the reason (400). */
export function useSaveExperienceBands() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (bands: ExperienceBandIn[]) =>
      unwrap(await api.PUT("/experience-bands", { body: { bands } })),
    onSuccess: (saved) => client.setQueryData(keys.experienceBands, saved),
  });
}

/** DESIGN §17.12: how many times more of a technical bucket a primary stack gets than a secondary one. */
export function usePrimaryShare() {
  return useQuery({
    queryKey: keys.primaryShare,
    queryFn: async () => unwrap(await api.GET("/experience-bands/primary-share")),
  });
}

export function useSavePrimaryShare() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (primary_share_weight: number) =>
      unwrap(await api.PUT("/experience-bands/primary-share", { body: { primary_share_weight } })),
    onSuccess: (saved) => client.setQueryData(keys.primaryShare, saved),
  });
}
